import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// The review URL is set by the business owner, so never redirect to anything but https
// (a javascript: URL would run on stellarreply.com)
const isSafeUrl = (url) => typeof url === 'string' && /^https:\/\//i.test(url);

export default function FeedbackFunnelPage() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Funnel state
  const [step, setStep] = useState('rate'); // rate | redirect | form | submitted
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  // Feedback form
  const [feedbackText, setFeedbackText] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const res = await fetch(`${API_URL}/feedback/funnel/${slug}`);
        if (!res.ok) throw new Error('Business not found');
        const data = await res.json();
        setBusiness(data);
      } catch {
        setError('This review link is not active. Please check the URL and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchBusiness();
  }, [slug]);

  const handleRate = (stars) => {
    setRating(stars);
    trackEvent('funnel_rated', { rating: stars, slug });
    if (stars >= 4) {
      // Happy customer — redirect to Google
      setStep('redirect');
      // Increment click counter via resolve endpoint
      fetch(`${API_URL}/review-link/resolve/${slug}`).catch(() => {});
      // Short delay then redirect
      setTimeout(() => {
        if (isSafeUrl(business.google_review_url)) window.location.href = business.google_review_url;
      }, 2000);
    } else {
      // Unhappy customer — show private feedback form
      setStep('form');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/feedback/funnel/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          feedback_text: feedbackText.trim() || null,
          customer_name: customerName.trim() || null,
          customer_email: customerEmail.trim() || null,
          customer_phone: customerPhone.trim() || null,
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      trackEvent('feedback_submitted', { rating, slug });
      setStep('submitted');
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = ['', 'Very Poor', 'Poor', 'Okay', 'Great', 'Excellent'];
  const activeRating = hoverRating || rating;

  if (loading) {
    return (
      <div style={styles.page}>
        <style>{cssText}</style>
        <div style={styles.card}>
          <div style={styles.spinner} className="funnel-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <style>{cssText}</style>
        <div style={styles.card}>
          <div style={styles.iconCircle}>
            <span style={{ fontSize: 28 }}>?</span>
          </div>
          <h1 style={styles.heading}>Link Not Found</h1>
          <p style={styles.sub}>{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      <div style={styles.card} className="funnel-card">
        {/* Logo */}
        <div style={styles.logoBar}>
          <div style={styles.logoIcon}>S</div>
          <span style={styles.logoText}>Stellar<span style={{ color: '#16a34a' }}>Reply</span></span>
        </div>

        {/* STEP: Rate */}
        {step === 'rate' && (
          <div className="funnel-fade">
            <h1 style={styles.heading}>How was your experience?</h1>
            <p style={styles.businessName}>{business.business_name}</p>
            <p style={styles.sub}>Your feedback helps us improve. Tap a star below.</p>

            <div style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map(i => (
                <span
                  key={i}
                  onClick={() => handleRate(i)}
                  onMouseEnter={() => setHoverRating(i)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    ...styles.star,
                    color: i <= activeRating ? '#f59e0b' : '#d4d1cc',
                    transform: i <= activeRating ? 'scale(1.15)' : 'scale(1)',
                  }}
                  className="funnel-star"
                >
                  &#9733;
                </span>
              ))}
            </div>
            {activeRating > 0 && (
              <p style={styles.ratingLabel} className="funnel-fade">
                {ratingLabels[activeRating]}
              </p>
            )}
          </div>
        )}

        {/* STEP: Redirect to Google (happy path) */}
        {step === 'redirect' && (
          <div className="funnel-fade">
            <div style={{ ...styles.iconCircle, background: 'rgba(22,163,74,0.1)' }}>
              <span style={{ fontSize: 28, color: '#16a34a' }}>&#10003;</span>
            </div>
            <h1 style={styles.heading}>We're glad you had a great experience!</h1>
            <p style={styles.sub}>
              Taking you to Google to share your review...
            </p>
            <div style={styles.spinner} className="funnel-spin" />
            <p style={{ ...styles.sub, fontSize: 12, marginTop: 16 }}>
              Not redirected? <a href={isSafeUrl(business.google_review_url) ? business.google_review_url : undefined} style={styles.link}>Click here</a>
            </p>
          </div>
        )}

        {/* STEP: Private feedback form (unhappy path) */}
        {step === 'form' && (
          <div className="funnel-fade">
            <h1 style={styles.heading}>We're sorry to hear that</h1>
            <p style={styles.sub}>
              We'd love the chance to make it right. Your feedback goes directly to the team at <strong>{business.business_name}</strong> and won't be posted publicly.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>What went wrong? <span style={styles.optional}>(optional)</span></label>
                <textarea
                  style={styles.textarea}
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  placeholder="Tell us about your experience so we can fix it..."
                  rows={4}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Your name <span style={styles.optional}>(optional)</span></label>
                <input
                  style={styles.input}
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="e.g. Sarah"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Email <span style={styles.optional}>(optional, so we can follow up personally)</span>
                </label>
                <input
                  style={styles.input}
                  type="email"
                  value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)}
                  placeholder="e.g. sarah@email.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Phone <span style={styles.optional}>(optional)</span>
                </label>
                <input
                  style={styles.input}
                  type="tel"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="e.g. 07700 900000"
                />
              </div>

              <button
                type="submit"
                style={{
                  ...styles.btn,
                  opacity: submitting ? 0.7 : 1,
                }}
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Feedback'}
              </button>

              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => { setStep('rate'); setRating(0); }}
              >
                Go back
              </button>
            </form>
          </div>
        )}

        {/* STEP: Thank you */}
        {step === 'submitted' && (
          <div className="funnel-fade">
            <div style={{ ...styles.iconCircle, background: 'rgba(22,163,74,0.1)' }}>
              <span style={{ fontSize: 28, color: '#16a34a' }}>&#10003;</span>
            </div>
            <h1 style={styles.heading}>Thank you for your feedback</h1>
            <p style={styles.sub}>
              Your message has been sent directly to the team at <strong>{business.business_name}</strong>. They'll use it to improve.
            </p>
            {customerEmail && (
              <p style={{ ...styles.sub, fontSize: 13 }}>
                If you left your contact details, someone may reach out to make things right.
              </p>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <p style={styles.footer}>
      Powered by <a href="https://stellarreply.com" style={styles.link}>StellarReply</a>
    </p>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

  @keyframes funnelFadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes funnelSpin {
    to { transform: rotate(360deg); }
  }
  .funnel-fade {
    animation: funnelFadeIn 0.4s ease both;
  }
  .funnel-spin {
    animation: funnelSpin 0.8s linear infinite;
  }
  .funnel-card {
    animation: funnelFadeIn 0.5s ease both;
  }
  .funnel-star {
    cursor: pointer;
    transition: transform 0.15s ease, color 0.15s ease;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }
  .funnel-star:hover {
    transform: scale(1.25) !important;
  }
`;

const styles = {
  page: {
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: '#f8f7f4',
  },
  card: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '40px 32px',
    maxWidth: 440,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 30px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
    border: '1px solid #ebe8e3',
  },
  logoBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 28,
  },
  logoIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    background: 'linear-gradient(135deg, #16a34a, #059669)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
  },
  logoText: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a1a',
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 8px',
    lineHeight: 1.3,
  },
  businessName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#16a34a',
    margin: '0 0 4px',
  },
  sub: {
    fontSize: 14,
    color: '#6b7280',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  starsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    margin: '8px 0 12px',
  },
  star: {
    fontSize: 44,
    lineHeight: 1,
    transition: 'transform 0.15s ease, color 0.15s ease',
  },
  ratingLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#f59e0b',
    margin: '0',
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'rgba(22,163,74,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  form: {
    textAlign: 'left',
    marginTop: 8,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
  },
  optional: {
    fontWeight: 400,
    color: '#9ca3af',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #e0ddd8',
    fontSize: 14,
    fontFamily: "'DM Sans', system-ui, sans-serif",
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #e0ddd8',
    fontSize: 14,
    fontFamily: "'DM Sans', system-ui, sans-serif",
    outline: 'none',
    resize: 'vertical',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '12px',
    borderRadius: 10,
    border: 'none',
    background: 'linear-gradient(135deg, #16a34a, #059669)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    fontFamily: "'DM Sans', system-ui, sans-serif",
    cursor: 'pointer',
    marginTop: 4,
  },
  btnGhost: {
    width: '100%',
    padding: '10px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "'DM Sans', system-ui, sans-serif",
    cursor: 'pointer',
    marginTop: 8,
  },
  spinner: {
    width: 24,
    height: 24,
    border: '3px solid #e0ddd8',
    borderTopColor: '#16a34a',
    borderRadius: '50%',
    margin: '16px auto',
  },
  link: {
    color: '#16a34a',
    textDecoration: 'none',
    fontWeight: 600,
  },
  footer: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 20,
    textAlign: 'center',
  },
};
